import axios, { AxiosResponse } from 'axios';

import { apiInterceptor } from 'services/axios';
import { API_URL } from 'shared/api/Constants';

import type {
  DocumentRawImagesApiResponse,
  DocumentRejectionReasonApiResponse,
  DocumentReview,
  DocumentReviewActionRequest,
  DocumentReviewApiResponse,
  DocumentReviewStatus,
  RejectDocumentPayload,
} from '../types/documentReview.types';

const getAuthHeaders = (): { Authorization: string | undefined } => {
  const token = sessionStorage.getItem('access-token')?.slice(1, -1);
  return {
    Authorization: token ? `Bearer ${token}` : undefined,
  };
};

const mapDocumentReview = (item: DocumentReviewApiResponse): DocumentReview => {
  const pdfAvailable = item.pdfAvailable ?? Boolean(item.generatedPdfUrl);
  const rawImagesAvailable = item.rawImagesAvailable ?? (item.rawImageCount ?? 0) > 0;

  return {
    documentId: item.documentId,
    patientId: item.patientId,
    patientName: item.patientName,
    uploadedDate: item.uploadedDate,
    documentName: item.documentName,
    documentDescription: item.documentDescription,
    reviewStatus: item.reviewStatus,
    rejectionReason: item.rejectionReason ?? item.reviewDecisionNotes,
    uploadedBy: item.uploadedBy,
    reviewedBy: item.reviewedBy,
    reviewedAt: item.reviewedAt,
    pdfAvailable,
    rawImagesAvailable,
    rawImageCount: item.rawImageCount ?? 0,
    generatedPdfUrl: pdfAvailable
      ? `${API_URL.DOCUMENT_REVIEW_BASE}/${item.documentId}/generated-pdf`
      : undefined,
    rawImages: [],
  };
};

const handleServiceError = (error: unknown, message: string): never => {
  console.error(message, error);
  throw new Error(message);
};

export const fetchDocumentReviewsAPI = async (
  status: DocumentReviewStatus
): Promise<DocumentReview[]> => {
  try {
    const response = await apiInterceptor.get<
      DocumentReviewApiResponse[],
      AxiosResponse<DocumentReviewApiResponse[]>
    >(API_URL.DOCUMENT_REVIEW_BASE, { params: { status } });
    return (response.data ?? []).map(mapDocumentReview);
  } catch (error) {
    return handleServiceError(error, 'Unable to load documents. Please try again.');
  }
};

export const fetchDocumentDetailsAPI = async (documentId: number): Promise<DocumentReview> => {
  try {
    const response = await apiInterceptor.get<
      DocumentReviewApiResponse,
      AxiosResponse<DocumentReviewApiResponse>
    >(`${API_URL.DOCUMENT_REVIEW_BASE}/${documentId}`);
    return mapDocumentReview(response.data);
  } catch (error) {
    return handleServiceError(error, 'Unable to load document details.');
  }
};

export const fetchRejectionReasonAPI = async (
  documentId: number
): Promise<DocumentRejectionReasonApiResponse> => {
  try {
    const response = await apiInterceptor.get<
      DocumentRejectionReasonApiResponse,
      AxiosResponse<DocumentRejectionReasonApiResponse>
    >(`${API_URL.DOCUMENT_REVIEW_BASE}/${documentId}/rejection-reason`);
    return response.data;
  } catch (error) {
    return handleServiceError(error, 'Unable to load rejection reason.');
  }
};

export const approveDocumentAPI = async (documentId: number): Promise<DocumentReview> => {
  const requestBody: DocumentReviewActionRequest = { action: 'APPROVE' };

  try {
    const response = await apiInterceptor.put<
      DocumentReviewActionRequest,
      AxiosResponse<DocumentReviewApiResponse>
    >(`${API_URL.DOCUMENT_REVIEW_BASE}/${documentId}`, requestBody);
    return mapDocumentReview(response.data);
  } catch (error) {
    return handleServiceError(error, 'Unable to approve the document.');
  }
};

export const rejectDocumentAPI = async ({
  documentId,
  rejectionReason,
}: RejectDocumentPayload): Promise<DocumentReview> => {
  const requestBody: DocumentReviewActionRequest = {
    action: 'REJECT',
    reviewDecisionNotes: rejectionReason,
  };

  try {
    const response = await apiInterceptor.put<
      DocumentReviewActionRequest,
      AxiosResponse<DocumentReviewApiResponse>
    >(`${API_URL.DOCUMENT_REVIEW_BASE}/${documentId}`, requestBody);
    return mapDocumentReview(response.data);
  } catch (error) {
    return handleServiceError(error, 'Unable to reject the document.');
  }
};

export const fetchGeneratedPdfBlobUrlAPI = async (documentId: number): Promise<string> => {
  try {
    const response = await axios.get<Blob>(
      `${API_URL.DOCUMENT_REVIEW_BASE}/${documentId}/generated-pdf`,
      {
        responseType: 'blob',
        headers: {
          ...getAuthHeaders(),
          Accept: 'application/pdf,*/*',
        },
      }
    );

    const contentType = response.headers['content-type'] ?? 'application/pdf';
    const blob = new Blob([response.data], { type: contentType });
    return URL.createObjectURL(blob);
  } catch (error) {
    return handleServiceError(error, 'Unable to load PDF.');
  }
};

export const fetchRawImagesAPI = async (documentId: number): Promise<string[]> => {
  try {
    const response = await apiInterceptor.get<
      DocumentRawImagesApiResponse,
      AxiosResponse<DocumentRawImagesApiResponse>
    >(`${API_URL.DOCUMENT_REVIEW_BASE}/${documentId}/raw-images`);
    return (response.data.images ?? []).map((image) => image.imageUrl);
  } catch (error) {
    return handleServiceError(error, 'Unable to load document images.');
  }
};

const buildImagesZipFileName = (documentName?: string): string => {
  if (!documentName?.trim()) {
    return 'document-images.zip';
  }
  return `${documentName.trim()}-Images.zip`;
};

export const downloadRawImagesZipAPI = async (
  documentId: number,
  documentName?: string
): Promise<void> => {
  try {
    const response = await axios.get<Blob>(
      `${API_URL.DOCUMENT_REVIEW_BASE}/${documentId}/raw-images/zip`,
      {
        responseType: 'blob',
        headers: {
          ...getAuthHeaders(),
          Accept: 'application/zip,*/*',
        },
      }
    );

    const contentType = response.headers['content-type'] ?? 'application/zip';
    const blob = new Blob([response.data], { type: contentType });
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = buildImagesZipFileName(documentName);
    link.click();
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    return handleServiceError(error, 'Unable to download document images.');
  }
};

export const softDeleteDocumentAPI = async (documentId: number): Promise<void> => {
  try {
    await apiInterceptor.delete(`${API_URL.DOCUMENT_REVIEW_BASE}/${documentId}`);
  } catch (error) {
    return handleServiceError(error, 'Unable to delete the document. Please try again.');
  }
};
